import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { apiErr } from "@/lib/api";
import { formatIDR, formatNumber } from "@/lib/format";
import { toast } from "sonner";

const GARMENTS = ["Kaos", "Poloshirt", "Kemeja", "Jaket", "Topi", "Patch/Emblem", "Seragam", "Tas"];
const PLACEMENTS = ["Dada Kiri", "Dada Kanan", "Punggung", "Lengan", "Topi Front", "Saku", "Kerah"];

const empty = {
  customer_name: "", phone: "", company: "", garment_type: "Kaos", placement: "Dada Kiri",
  design_name: "", stitch_count: 0, thread_colors: [], qty: 1, rate_per_1000: 1200,
  setup_fee: 50000, addons_fee: 0, dp: 0, payment_status: "Belum Bayar",
  status: "Design & Punching", machine_id: "", notes: "", target_date: "",
};

export default function OrderDialog({ open, onOpenChange, order, prefill, onSaved }) {
  const [form, setForm] = useState(empty);
  const [machines, setMachines] = useState([]);
  const [colorsText, setColorsText] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/machines").then((r) => setMachines(r.data)).catch(() => {});
    const base = order ? { ...empty, ...order } : prefill ? { ...empty, ...prefill } : empty;
    setForm(base);
    setColorsText((base.thread_colors || []).join(", "));
  }, [open, order, prefill]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const total = Math.round((form.stitch_count / 1000) * form.rate_per_1000 * form.qty + Number(form.setup_fee) + Number(form.addons_fee));
  const remaining = total - Number(form.dp || 0);
  const autoPay = Number(form.dp) <= 0 ? "Belum Bayar" : Number(form.dp) >= total ? "Lunas" : "DP";

  const save = async () => {
    if (!form.customer_name) { toast.error("Nama pelanggan wajib diisi"); return; }
    setSaving(true);
    const payload = {
      ...form,
      stitch_count: Number(form.stitch_count),
      qty: Number(form.qty),
      rate_per_1000: Number(form.rate_per_1000),
      setup_fee: Number(form.setup_fee),
      addons_fee: Number(form.addons_fee),
      dp: Number(form.dp),
      total,
      payment_status: autoPay,
      thread_colors: colorsText.split(",").map((s) => s.trim()).filter(Boolean),
      target_date: form.target_date ? new Date(form.target_date).toISOString() : null,
    };
    try {
      if (order) await api.put(`/orders/${order.id}`, payload);
      else await api.post("/orders", payload);
      toast.success(order ? "Order diperbarui" : "Order baru dibuat");
      onSaved?.();
      onOpenChange(false);
    } catch (e) { toast.error(apiErr(e)); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">{order ? "Edit Order" : "Terima Pesanan Baru"}</DialogTitle></DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label>Nama Pelanggan *</Label>
            <Input data-testid="order-customer-name" value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>No. WhatsApp / Telepon</Label>
            <Input data-testid="order-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="6281..." />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label>Perusahaan / Komunitas</Label>
            <Input data-testid="order-company" value={form.company} onChange={(e) => set("company", e.target.value)} />
          </div>

          <div className="space-y-1.5">
            <Label>Jenis Garment</Label>
            <Select value={form.garment_type} onValueChange={(v) => set("garment_type", v)}>
              <SelectTrigger data-testid="order-garment"><SelectValue /></SelectTrigger>
              <SelectContent>{GARMENTS.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Posisi Bordir</Label>
            <Select value={form.placement} onValueChange={(v) => set("placement", v)}>
              <SelectTrigger data-testid="order-placement"><SelectValue /></SelectTrigger>
              <SelectContent>{PLACEMENTS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5 sm:col-span-2">
            <Label>Nama Desain</Label>
            <Input data-testid="order-design-name" value={form.design_name} onChange={(e) => set("design_name", e.target.value)} />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label>Warna Benang (pisahkan dengan koma)</Label>
            <Input data-testid="order-colors" value={colorsText} onChange={(e) => setColorsText(e.target.value)} placeholder="Navy RY-1134, Gold MT-GOLD" />
          </div>

          <div className="space-y-1.5">
            <Label>Jumlah Stitch</Label>
            <Input data-testid="order-stitch" type="number" value={form.stitch_count} onChange={(e) => set("stitch_count", e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Qty (pcs)</Label>
            <Input data-testid="order-qty" type="number" value={form.qty} onChange={(e) => set("qty", e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Tarif / 1000 stitch</Label>
            <Input data-testid="order-rate" type="number" value={form.rate_per_1000} onChange={(e) => set("rate_per_1000", e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Biaya Setup / Film</Label>
            <Input data-testid="order-setup" type="number" value={form.setup_fee} onChange={(e) => set("setup_fee", e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Biaya Tambahan</Label>
            <Input data-testid="order-addons" type="number" value={form.addons_fee} onChange={(e) => set("addons_fee", e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>DP Dibayar</Label>
            <Input data-testid="order-dp" type="number" value={form.dp} onChange={(e) => set("dp", e.target.value)} />
          </div>

          <div className="space-y-1.5">
            <Label>Mesin</Label>
            <Select value={form.machine_id} onValueChange={(v) => set("machine_id", v)}>
              <SelectTrigger data-testid="order-machine"><SelectValue placeholder="Pilih mesin" /></SelectTrigger>
              <SelectContent>{machines.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Target Selesai</Label>
            <Input data-testid="order-target" type="date" value={form.target_date ? String(form.target_date).slice(0, 10) : ""} onChange={(e) => set("target_date", e.target.value)} />
          </div>

          <div className="space-y-1.5 sm:col-span-2">
            <Label>Catatan</Label>
            <Textarea data-testid="order-notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} />
          </div>
        </div>

        <div className="rounded-lg bg-slate-50 border border-slate-200 p-4 space-y-1">
          <div className="flex justify-between text-sm"><span className="text-slate-500">Grand Total</span><span className="font-heading text-lg font-extrabold text-slate-900" data-testid="order-total">{formatIDR(total)}</span></div>
          <div className="flex justify-between text-sm"><span className="text-slate-500">Sisa Tagihan</span><span className="font-semibold text-rose-600">{formatIDR(remaining)}</span></div>
          <div className="flex justify-between text-sm"><span className="text-slate-500">Status Pembayaran</span><span className="font-semibold">{autoPay}</span></div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Batal</Button>
          <Button data-testid="btn-save-order" className="bg-blue-600 hover:bg-blue-700" onClick={save} disabled={saving}>
            {saving ? "Menyimpan..." : order ? "Simpan Perubahan" : "Buat Order"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
